
import { useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box, Flex, Paper, Skeleton } from '@mantine/core';
import { useTranslation } from "react-i18next";

export const TinyMCEEditor = ({ defaultValue = '', value, onChange, ...props }) => {

    const { t } = useTranslation();

    const editorRef = useRef(null);
    const [isEditorLoaded, setIsEditorLoaded] = useState(() => false);

    return (
        <>
            <Box>
                <Editor
                    tinymceScriptSrc="/js/react/tinymce/tinymce.min.js"
                    onInit={(_evt, editor) => {
                        editorRef.current = editor;
                        setIsEditorLoaded(true);
                    }}
                    onEditorChange={onChange}
                    initialValue={defaultValue}
                    //value={value}
                    init={{
                        license_key: 'gpl',
                        height: 300,
                        menubar: false,
                        plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                            'anchor', 'searchreplace', 'code', 'fullscreen', 'code', 'wordcount', 'emoticons'
                        ],
                        toolbar: 'bold italic underline strikethrough customcase '  + (props?.hasLegalText ? 'legaltext | ' : '') +
                            'align blocks | bullist numlist | emoticons link | ' +
                            'undo redo code',
                        content_style: 'body',
                        branding: false,
                        link_default_target: '_blank',
                        link_target_list: [
                            { text: t('editor.newWindow'), value: '_blank' }
                        ],
                        setup: (editor) => {
                            const applyCaseChange = (transformFn) => {
                                const selectedText = editor.selection.getContent({ format: "text" });
                                if (!selectedText) return;

                                const transformed = transformFn(selectedText);
                                editor.selection.setContent(transformed);
                            };

                            const toTitleCase = (str) => {
                                return str.replace(/\w\S*/g, (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                );
                            };

                            const toSentenceCase = (str = '') => {
                                return str
                                    .toLowerCase()
                                    .replace(/(^\s*[a-zа-я]|[.!?]\s*[a-zа-я])/giu, (c) => c.toUpperCase());
                            };

                            editor.ui.registry.addMenuButton("customcase", {
                                icon: "change-case",
                                tooltip: "Change case",
                                fetch: (callback) => {
                                    callback([
                                        {
                                            type: "menuitem",
                                            text: "UPPERCASE",
                                            onAction: () => applyCaseChange((t) => t.toUpperCase()),
                                        },
                                        {
                                            type: "menuitem",
                                            text: "lowercase",
                                            onAction: () => applyCaseChange((t) => t.toLowerCase()),
                                        },
                                        {
                                            type: "menuitem",
                                            text: t('editor.titleCase'),
                                            onAction: () => applyCaseChange(toTitleCase),
                                        },
                                        {
                                            type: "menuitem",
                                            text: t('editor.sentenceCase'),
                                            onAction: () => applyCaseChange(toSentenceCase),
                                        },
                                    ]);
                                },
                            });


                            editor.ui.registry.addToggleButton("legaltext", {
                                icon: "info",
                                tooltip: "Legal Text",
                                onAction: () => { 
                                        if (!editor.formatter.has('legalText')) { 
                                            editor.formatter.register('legalText', { block: 'p', styles: { 'font-size': '12px', 'font-style': 'italic', 'text-align': 'center', } }); 
                                        } 
                                        editor.formatter.toggle('legalText'); 
                                },
                                onSetup: (api) => {
                                    const handler = () => {
                                        api.setActive(editor.formatter.match('legalText'));
                                    };

                                    editor.on('NodeChange', handler);

                                    return () => {
                                        editor.off('NodeChange', handler);
                                    };
                                }
                            });
                        }
                    }}
                    {...props}
                />
            </Box>
            {
                !isEditorLoaded && (<TinyMCESkeleton />)
            }
        </>
    )
}

const TinyMCESkeleton = () => {
    return (
        <Paper shadow="md" radius="md" withBorder h={300}>
            <Skeleton h={50} radius="md" style={{ borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }} />
            <Box p="md">
                <Flex gap="md">
                    <Skeleton h={15} w="70%" />
                    <Skeleton h={15} w="30%" />
                </Flex>

                <Flex gap="md" mt="xs">
                    <Skeleton h={15} w="40%" />
                    <Skeleton h={15} w="20%" />
                    <Skeleton h={15} w="40%" />
                </Flex>

                <Flex gap="md" mt="xs">
                    <Skeleton h={15} w="30%" />
                    <Skeleton h={15} w="50%" />
                    <Skeleton h={15} w="20%" />
                </Flex>

                <Flex gap="sm" mt="xs">
                    <Skeleton h={15} w="50%" />
                    <Skeleton h={15} w="50%" />
                </Flex>

                <Flex gap="md" mt="xs">
                    <Skeleton h={15} w="40%" />
                    <Skeleton h={15} w="20%" />
                    <Skeleton h={15} w="40%" />
                </Flex>

                <Flex gap="md" mt="xs">
                    <Skeleton h={15} w="70%" />
                    <Skeleton h={15} w="30%" />
                </Flex>
            </Box>

        </Paper>
    )
}